import React, { useState, useCallback } from 'react';
import { apiClient } from '../../../api/client';
import { TEXT_MUTED, TEXT_DARK, PRIMARY, BORDER_LIGHT } from './designTokens';
import { PrintTablesReport } from './PrintTablesReport';
import { DailyActivityReport } from './DailyActivityReport';

interface AuditEntry {
  id: string;
  action: string;
  userName: string;
  tableId: string | null;
  seatId: string | null;
  guestId: string | null;
  previousValue: string | null;
  newValue: string | null;
  metadata: string | null;
  createdAt: string;
}

interface AuditLogResponse {
  entries: AuditEntry[];
  total: number;
  page: number;
  totalPages: number;
}

interface OccupancyRow {
  zoneName: string;
  colorCode: string;
  totalSeats: number;
  occupiedSeats: number;
  availableSeats: number;
  blockedSeats: number;
  heldSeats: number;
  occupancyPercentage: number;
}

interface GuestListRow {
  guestName: string;
  seatNumber: number;
  seatCompositeId: string;
  checkedIn: boolean;
  dietaryPreference: string | null;
  seatingPreferences: string | null;
  chapterAffiliation: string | null;
  familyGroup: string | null;
}

interface UnassignedGuest {
  id: string;
  fullName: string;
  chapterAffiliation: string | null;
  familyAssociation: string | null;
  seatingPreferences: string | null;
  accessibilityNeeds: string | null;
  ticketNumber: string | null;
  referenceId: string | null;
}

interface CheckInStatusRow {
  guestId: string;
  name: string;
  tableNumber: number;
  tableName: string;
  seatNumber: number;
  compositeId: string;
  checkInStatus: 'checked-in' | 'not-arrived';
  arrivalTimestamp: string | null;
}

type ReportView = 'audit-log' | 'occupancy' | 'guest-list' | 'unassigned' | 'check-in-status' | 'seats-hold' | 'print-tables' | 'chapter-distribution' | 'ai-insights' | 'daily-report';

const styles: Record<string, React.CSSProperties> = {
  container: {},
  tabs: {
    display: 'flex',
    gap: '0',
    marginBottom: '1rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    flexWrap: 'wrap',
  },
  tab: {
    padding: '0.45rem 0.85rem',
    fontSize: '0.78rem',
    fontWeight: 500,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: TEXT_MUTED,
    borderBottom: '2px solid transparent',
    marginBottom: '-1px',
  },
  tabActive: {
    padding: '0.45rem 0.85rem',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: PRIMARY,
    borderBottom: `2px solid ${PRIMARY}`,
    marginBottom: '-1px',
  },
  filterRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '0.75rem',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  input: {
    padding: '0.4rem 0.65rem',
    fontSize: '0.82rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    outline: 'none',
    minWidth: '140px',
  },
  searchBtn: {
    padding: '0.4rem 0.85rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.82rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.5rem 0.65rem',
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e0e0e0',
    fontWeight: 600,
    color: '#333',
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
  },
  td: {
    padding: '0.45rem 0.65rem',
    borderBottom: '1px solid #f0f0f0',
    color: '#444',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.6rem 0',
    fontSize: '0.78rem',
    color: '#666',
  },
  pageBtn: {
    padding: '0.3rem 0.6rem',
    fontSize: '0.75rem',
    border: '1px solid #ccc',
    borderRadius: '3px',
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#666',
    fontSize: '0.85rem',
  },
  error: {
    padding: '0.5rem 0.75rem',
    color: '#d32f2f',
    fontSize: '0.8rem',
    backgroundColor: '#fdecea',
    borderRadius: '4px',
    marginBottom: '0.75rem',
  },
  empty: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#888',
    fontSize: '0.85rem',
  },
  zoneDot: {
    display: 'inline-block',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    marginRight: '0.4rem',
  },
  statusBadge: {
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '0.1rem 0.4rem',
    borderRadius: '3px',
  },
  selectInput: {
    padding: '0.4rem 0.65rem',
    fontSize: '0.82rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    outline: 'none',
    backgroundColor: '#fff',
    minWidth: '120px',
  },
};

// Audit Log Sub-component
function AuditLogView() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tableFilter, setTableFilter] = useState('');
  const [seatFilter, setSeatFilter] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const fetchLog = useCallback(async (pageNum: number = 1) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: String(pageNum), limit: '200' });
      if (tableFilter.trim()) params.set('tableId', tableFilter.trim());
      if (seatFilter.trim()) params.set('seatId', seatFilter.trim());

      const data = await apiClient<AuditLogResponse>(
        `/table-management/audit-log?${params.toString()}`
      );
      setEntries(data.entries);
      setPage(data.page);
      setTotalPages(data.totalPages);
      setHasSearched(true);
    } catch {
      setError('Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  }, [tableFilter, seatFilter]);

  const formatAction = (action: string) => {
    return action.replace(/([A-Z])/g, ' $1').trim();
  };

  return (
    <div>
      <div style={styles.filterRow}>
        <input
          style={styles.input}
          type="text"
          value={tableFilter}
          onChange={(e) => setTableFilter(e.target.value)}
          placeholder="Filter by table ID..."
          aria-label="Filter by table ID"
        />
        <input
          style={styles.input}
          type="text"
          value={seatFilter}
          onChange={(e) => setSeatFilter(e.target.value)}
          placeholder="Filter by seat ID..."
          aria-label="Filter by seat ID"
        />
        <button style={styles.searchBtn} onClick={() => fetchLog(1)} aria-label="Search audit log">
          Search
        </button>
      </div>

      {error && <div style={styles.error} role="alert">{error}</div>}
      {loading && <div style={styles.loading}>Loading audit log...</div>}

      {!loading && hasSearched && entries.length === 0 && (
        <div style={styles.empty}>No audit log entries found.</div>
      )}

      {!loading && entries.length > 0 && (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table} aria-label="Audit log entries">
              <thead>
                <tr>
                  <th style={styles.th}>Timestamp</th>
                  <th style={styles.th}>Action</th>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Previous</th>
                  <th style={styles.th}>New</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td style={styles.td}>
                      {new Date(entry.createdAt).toLocaleString([], {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </td>
                    <td style={styles.td}>{formatAction(entry.action)}</td>
                    <td style={styles.td}>{entry.userName}</td>
                    <td style={{ ...styles.td, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {entry.previousValue || '—'}
                    </td>
                    <td style={{ ...styles.td, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {entry.newValue || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                style={styles.pageBtn}
                onClick={() => fetchLog(page - 1)}
                disabled={page <= 1}
              >
                ← Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                style={styles.pageBtn}
                onClick={() => fetchLog(page + 1)}
                disabled={page >= totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Occupancy Report Sub-component
function OccupancyReport() {
  const [data, setData] = useState<OccupancyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiClient<OccupancyRow[]>('/table-management/reports/occupancy');
      setData(result);
    } catch {
      setError('Failed to load occupancy report.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (loading) return <div style={styles.loading}>Loading occupancy report...</div>;
  if (error) return <div style={styles.error} role="alert">{error}</div>;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={styles.table} aria-label="Occupancy report by zone">
        <thead>
          <tr>
            <th style={styles.th}>Zone</th>
            <th style={styles.th}>Total Seats</th>
            <th style={styles.th}>Occupied</th>
            <th style={styles.th}>Available</th>
            <th style={styles.th}>Held</th>
            <th style={styles.th}>Blocked</th>
            <th style={styles.th}>Occupancy %</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.zoneName}>
              <td style={styles.td}>
                <span style={{ ...styles.zoneDot, backgroundColor: row.colorCode }} />
                {row.zoneName}
              </td>
              <td style={styles.td}>{row.totalSeats}</td>
              <td style={styles.td}>{row.occupiedSeats}</td>
              <td style={styles.td}>{row.availableSeats}</td>
              <td style={styles.td}>{row.heldSeats || 0}</td>
              <td style={styles.td}>{row.blockedSeats}</td>
              <td style={styles.td}>
                <strong>{row.occupancyPercentage.toFixed(1)}%</strong>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Chapter Distribution Report Sub-component
interface ChapterDistGuest {
  fullName: string;
  ticketNumber: string | null;
  orderReferenceId: string | null;
  tableName: string | null;
  tableNumber: number | null;
  seatNumber: number | null;
  zoneName: string | null;
  tierName?: string | null;
  isSeated: boolean;
}

interface ChapterDistRow {
  chapter: string;
  inceptionYear: number | null;
  totalGuests: number;
  seatedGuests: number;
  unseatedGuests: number;
  tableCount: number;
  zoneCount: number;
  guests: ChapterDistGuest[];
}

function ChapterDistributionReport() {
  const [data, setData] = useState<ChapterDistRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedChapter, setSelectedChapter] = useState<string | null>(null);
  const [detailSort, setDetailSort] = useState<'table' | 'order'>('table');
  const [summarySort, setSummarySort] = useState<'total' | 'chapter' | 'inception'>('total');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiClient<ChapterDistRow[]>('/table-management/reports/chapter-distribution');
      setData(result);
    } catch {
      setError('Failed to load chapter distribution report.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handlePrintSummary = () => {
    const totalGuests = data.reduce((s, r) => s + r.totalGuests, 0);
    const totalSeated = data.reduce((s, r) => s + r.seatedGuests, 0);
    const headerRow1 = '<tr><th rowspan="2">Chapter</th><th rowspan="2" style="text-align:center">Inception</th><th colspan="3" style="text-align:center;border-bottom:none">All Guests</th><th colspan="3" style="text-align:center;border-bottom:none">Patrons</th><th rowspan="2" style="text-align:center">Total Tables</th><th rowspan="2" style="text-align:center">Zones</th></tr>';
    const headerRow2 = '<tr><th style="text-align:center">Total</th><th style="text-align:center">Seated</th><th style="text-align:center">Unseated</th><th style="text-align:center">Total</th><th style="text-align:center">Seated</th><th style="text-align:center">Unseated</th></tr>';
    const rows = sortedData.map(r => {
      const patrons = r.guests.filter(g => g.tierName && g.tierName.toLowerCase() === 'patron');
      const pSeated = patrons.filter(g => g.isSeated).length;
      const pUnseated = patrons.length - pSeated;
      return `<tr><td>${r.chapter}</td><td style="text-align:center">${r.inceptionYear || '—'}</td><td style="text-align:center">${r.totalGuests}</td><td style="text-align:center">${r.seatedGuests}</td><td style="text-align:center">${r.unseatedGuests}</td><td style="text-align:center">${patrons.length}</td><td style="text-align:center">${pSeated}</td><td style="text-align:center">${pUnseated}</td><td style="text-align:center">${r.tableCount}</td><td style="text-align:center">${r.zoneCount}</td></tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><title>Chapter Distribution Summary</title><style>body{font-family:sans-serif;padding:20px;position:relative;}table{width:100%;border-collapse:collapse;}th{text-align:left;padding:8px 10px;border-bottom:2px solid #333;font-size:13px;}td{padding:6px 10px;border-bottom:1px solid #ddd;font-size:12px;}h1{font-size:18px;margin-bottom:4px;}p{color:#666;font-size:12px;margin-bottom:16px;}.watermark{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);opacity:0.06;width:400px;height:auto;pointer-events:none;z-index:-1;}</style></head><body><img class="watermark" src="/KDULogo.PNG" alt="" /><h1>Chapter Distribution — Summary</h1><p>${data.length} chapters • ${totalGuests} guests (${totalSeated} seated) • Printed ${new Date().toLocaleDateString()}</p><table><thead>${headerRow1}${headerRow2}</thead><tbody>${rows}</tbody></table></body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
  };

  const handlePrintExpanded = () => {
    let sections = '';
    for (const row of data) {
      const patrons = row.guests.filter(g => g.tierName && g.tierName.toLowerCase() === 'patron');
      const pSeated = patrons.filter(g => g.isSeated).length;
      const guestRows = row.guests.map(g => `<tr><td>${g.fullName}</td><td style="text-align:center">${g.tierName || 'General'}</td><td>${g.orderReferenceId || '—'}</td><td>${g.ticketNumber || '—'}</td><td>${g.tableName || (g.tableNumber != null ? `Table ${g.tableNumber}` : '—')}</td><td style="text-align:center">${g.seatNumber ?? '—'}</td><td>${g.zoneName || '—'}</td></tr>`).join('');
      sections += `<div style="margin-bottom:20px;"><h3 style="font-size:14px;margin:0 0 6px;border-bottom:1px solid #333;padding-bottom:4px;">${row.chapter} (${row.totalGuests} guests, ${row.seatedGuests} seated, ${patrons.length} patrons [${pSeated} seated])</h3><table><thead><tr><th>Guest Name</th><th style="text-align:center">Tier</th><th>Order ID</th><th>Ticket #</th><th>Table Name</th><th style="text-align:center">Seat #</th><th>Zone</th></tr></thead><tbody>${guestRows}</tbody></table></div>`;
    }
    const totalGuests = data.reduce((s, r) => s + r.totalGuests, 0);
    const html = `<!DOCTYPE html><html><head><title>Chapter Distribution — Expanded</title><style>body{font-family:sans-serif;padding:20px;position:relative;}table{width:100%;border-collapse:collapse;margin-bottom:8px;}th{text-align:left;padding:6px 8px;border-bottom:2px solid #333;font-size:11px;text-transform:uppercase;}td{padding:5px 8px;border-bottom:1px solid #ddd;font-size:11px;}h1{font-size:18px;margin-bottom:4px;}h3{page-break-before:auto;}p{color:#666;font-size:12px;margin-bottom:16px;}.watermark{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);opacity:0.06;width:400px;height:auto;pointer-events:none;z-index:-1;}</style></head><body><img class="watermark" src="/KDULogo.PNG" alt="" /><h1>Chapter Distribution — Expanded Report</h1><p>${data.length} chapters • ${totalGuests} guests • Printed ${new Date().toLocaleDateString()}</p>${sections}</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
  };

  if (loading) return <div style={styles.loading}>Loading chapter distribution...</div>;
  if (error) return <div style={styles.error} role="alert">{error}</div>;

  const totalGuests = data.reduce((sum, r) => sum + r.totalGuests, 0);
  const totalSeated = data.reduce((sum, r) => sum + r.seatedGuests, 0);
  const totalUnseated = data.reduce((sum, r) => sum + r.unseatedGuests, 0);

  const sortedData = [...data].sort((a, b) => {
    if (summarySort === 'chapter') return a.chapter.localeCompare(b.chapter);
    if (summarySort === 'inception') return (a.inceptionYear || 9999) - (b.inceptionYear || 9999);
    return b.totalGuests - a.totalGuests; // 'total' default
  });

  const selectedData = selectedChapter ? data.find(r => r.chapter === selectedChapter) : null;

  return (
    <div>
      {/* Header with print buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ fontSize: '0.82rem', color: TEXT_MUTED }}>
          {data.length} chapters • {totalGuests} guests ({totalSeated} seated, {totalUnseated} unseated)
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: '0.7rem', color: TEXT_MUTED }}>Sort:</span>
          <button style={summarySort === 'total' ? styles.tabActive : styles.tab} onClick={() => setSummarySort('total')}>Total</button>
          <button style={summarySort === 'chapter' ? styles.tabActive : styles.tab} onClick={() => setSummarySort('chapter')}>Name</button>
          <button style={summarySort === 'inception' ? styles.tabActive : styles.tab} onClick={() => setSummarySort('inception')}>Inception</button>
          <span style={{ margin: '0 4px', color: '#ddd' }}>|</span>
          <button style={{ ...styles.searchBtn, fontSize: '0.72rem', padding: '4px 10px' }} onClick={handlePrintSummary} aria-label="Print summary report">🖨️ Summary</button>
          <button style={{ ...styles.searchBtn, fontSize: '0.72rem', padding: '4px 10px' }} onClick={handlePrintExpanded} aria-label="Print expanded report">🖨️ Expanded</button>
        </div>
      </div>

      {/* Drill-down back button */}
      {selectedChapter && (
        <button
          onClick={() => setSelectedChapter(null)}
          style={{ background: 'none', border: 'none', color: PRIMARY, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', padding: '0 0 8px', marginBottom: '4px' }}
          aria-label="Back to chapter list"
        >
          ← Back to Chapters
        </button>
      )}

      {/* Summary table */}
      {!selectedChapter && (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table} aria-label="Chapter distribution matrix">
            <thead>
              <tr>
                <th style={styles.th} rowSpan={2}>Chapter</th>
                <th style={{ ...styles.th, textAlign: 'center' }} rowSpan={2}>Inception</th>
                <th style={{ ...styles.th, textAlign: 'center', borderBottom: 'none' }} colSpan={3}>All Guests</th>
                <th style={{ ...styles.th, textAlign: 'center', borderBottom: 'none' }} colSpan={3}>Patrons</th>
                <th style={{ ...styles.th, textAlign: 'center' }} rowSpan={2}>Total Tables</th>
                <th style={{ ...styles.th, textAlign: 'center' }} rowSpan={2}>Zones</th>
              </tr>
              <tr>
                <th style={{ ...styles.th, textAlign: 'center', fontSize: '0.7rem' }}>Total</th>
                <th style={{ ...styles.th, textAlign: 'center', fontSize: '0.7rem' }}>Seated</th>
                <th style={{ ...styles.th, textAlign: 'center', fontSize: '0.7rem' }}>Unseated</th>
                <th style={{ ...styles.th, textAlign: 'center', fontSize: '0.7rem' }}>Total</th>
                <th style={{ ...styles.th, textAlign: 'center', fontSize: '0.7rem' }}>Seated</th>
                <th style={{ ...styles.th, textAlign: 'center', fontSize: '0.7rem' }}>Unseated</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((row) => {
                const patrons = row.guests.filter(g => g.tierName && g.tierName.toLowerCase() === 'patron');
                const patronCount = patrons.length;
                const patronsSeated = patrons.filter(g => g.isSeated).length;
                const patronsUnseated = patronCount - patronsSeated;
                return (
                <tr
                  key={row.chapter}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedChapter(row.chapter)}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                >
                  <td style={{ ...styles.td, fontWeight: 600, color: PRIMARY }}>{row.chapter}</td>
                  <td style={{ ...styles.td, textAlign: 'center', color: row.inceptionYear ? TEXT_DARK : '#bbb' }}>{row.inceptionYear || '—'}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{row.totalGuests}</td>
                  <td style={{ ...styles.td, textAlign: 'center', color: '#2e7d32' }}>{row.seatedGuests}</td>
                  <td style={{ ...styles.td, textAlign: 'center', color: row.unseatedGuests > 0 ? '#c62828' : '#999' }}>{row.unseatedGuests}</td>
                  <td style={{ ...styles.td, textAlign: 'center', color: patronCount > 0 ? '#8B0000' : '#999', fontWeight: patronCount > 0 ? 600 : 400 }}>{patronCount}</td>
                  <td style={{ ...styles.td, textAlign: 'center', color: patronsSeated > 0 ? '#2e7d32' : '#999' }}>{patronsSeated}</td>
                  <td style={{ ...styles.td, textAlign: 'center', color: patronsUnseated > 0 ? '#c62828' : '#999' }}>{patronsUnseated}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{row.tableCount}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{row.zoneCount}</td>
                </tr>
                );
              })}
              {sortedData.length > 1 && (
                <tr style={{ borderTop: '2px solid #333', fontWeight: 700 }}>
                  <td style={{ ...styles.td, fontWeight: 700 }}>Total</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>—</td>
                  <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700 }}>{totalGuests}</td>
                  <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700, color: '#2e7d32' }}>{totalSeated}</td>
                  <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700, color: totalUnseated > 0 ? '#c62828' : '#999' }}>{totalUnseated}</td>
                  <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700, color: '#8B0000' }}>{data.reduce((s, r) => s + r.guests.filter(g => g.tierName && g.tierName.toLowerCase() === 'patron').length, 0)}</td>
                  <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700, color: '#2e7d32' }}>{data.reduce((s, r) => s + r.guests.filter(g => g.tierName && g.tierName.toLowerCase() === 'patron' && g.isSeated).length, 0)}</td>
                  <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700, color: '#c62828' }}>{data.reduce((s, r) => s + r.guests.filter(g => g.tierName && g.tierName.toLowerCase() === 'patron' && !g.isSeated).length, 0)}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>—</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>—</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Drill-down detail */}
      {selectedData && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: TEXT_DARK }}>
              {selectedData.chapter} — {selectedData.totalGuests} guest{selectedData.totalGuests !== 1 ? 's' : ''} ({selectedData.seatedGuests} seated, {selectedData.unseatedGuests} unseated)
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{ fontSize: '0.72rem', color: TEXT_MUTED }}>Sort:</span>
              <button
                style={detailSort === 'table' ? styles.tabActive : styles.tab}
                onClick={() => setDetailSort('table')}
              >Table #</button>
              <button
                style={detailSort === 'order' ? styles.tabActive : styles.tab}
                onClick={() => setDetailSort('order')}
              >Order ID</button>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table} aria-label={`Guests for ${selectedData.chapter}`}>
              <thead>
                <tr>
                  <th style={styles.th}>Guest Name</th>
                  <th style={styles.th}>Order ID</th>
                  <th style={styles.th}>Ticket #</th>
                  <th style={styles.th}>Table Name</th>
                  <th style={styles.th}>Table #</th>
                  <th style={styles.th}>Seat #</th>
                  <th style={styles.th}>Zone</th>
                </tr>
              </thead>
              <tbody>
                {[...selectedData.guests].sort((a, b) => {
                  if (detailSort === 'order') {
                    return (a.orderReferenceId || 'zzz').localeCompare(b.orderReferenceId || 'zzz');
                  }
                  // table sort: seated first, then by table number, then seat
                  if (a.isSeated !== b.isSeated) return a.isSeated ? -1 : 1;
                  if (a.tableNumber !== b.tableNumber) return (a.tableNumber || 999) - (b.tableNumber || 999);
                  return (a.seatNumber || 0) - (b.seatNumber || 0);
                }).map((g, idx) => (
                  <tr key={idx} style={{ backgroundColor: g.isSeated ? '' : '#fff8f8' }}>
                    <td style={{ ...styles.td, fontWeight: 500 }}>{g.fullName}</td>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.75rem', color: g.orderReferenceId ? '#1565c0' : '#bbb' }}>{g.orderReferenceId || '—'}</td>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.78rem' }}>{g.ticketNumber || '—'}</td>
                    <td style={styles.td}>{g.tableName || '—'}</td>
                    <td style={styles.td}>{g.tableNumber ?? '—'}</td>
                    <td style={styles.td}>{g.seatNumber ?? '—'}</td>
                    <td style={styles.td}>{g.zoneName || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Guest List Report Sub-component
function GuestListReport() {
  const [tables, setTables] = useState<Array<{ id: string; tableNumber: number; tableName: string | null }>>([]);
  const [selectedTableId, setSelectedTableId] = useState('');
  const [data, setData] = useState<GuestListRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Fetch all tables on mount for dropdown
  React.useEffect(() => {
    apiClient<Array<{ id: string; tableNumber: number; tableName: string | null }>>('/table-management/tables')
      .then((result) => setTables(Array.isArray(result) ? result.sort((a, b) => a.tableNumber - b.tableNumber) : []))
      .catch(() => {});
  }, []);

  const fetchReport = useCallback(async () => {
    if (!selectedTableId) return;
    setLoading(true);
    setError('');
    try {
      const result = await apiClient<{ guests: Array<{ guestId: string; name: string; seatNumber: number; compositeId: string; checkedIn: boolean; dietaryRestrictions: string | null; seatingPreferences: string | null; chapterAffiliation: string | null; familyAssociation: string | null }> }>(
        `/table-management/reports/table/${selectedTableId}/guests`
      );
      const mapped: GuestListRow[] = (result.guests || []).map((g) => ({
        guestName: g.name,
        seatNumber: g.seatNumber,
        seatCompositeId: g.compositeId,
        checkedIn: g.checkedIn,
        dietaryPreference: g.dietaryRestrictions,
        seatingPreferences: g.seatingPreferences,
        chapterAffiliation: g.chapterAffiliation,
        familyGroup: g.familyAssociation,
      }));
      setData(mapped);
      setHasSearched(true);
    } catch {
      setError('Failed to load guest list report.');
    } finally {
      setLoading(false);
    }
  }, [selectedTableId]);

  const [tableFilter, setTableFilter] = useState('');

  const filteredTables = tables.filter((t) => {
    if (!tableFilter) return true;
    const q = tableFilter.toLowerCase();
    return (
      (t.tableName || '').toLowerCase().includes(q) ||
      String(t.tableNumber).includes(q)
    );
  });

  return (
    <div>
      <div style={styles.filterRow}>
        <input
          type="text"
          style={{ ...styles.input, minWidth: '160px', marginRight: '0.5rem' }}
          placeholder="Search by table name or number..."
          value={tableFilter}
          onChange={(e) => setTableFilter(e.target.value)}
          aria-label="Filter tables by name or number"
        />
        <select
          style={{ ...styles.input, minWidth: '200px' }}
          value={selectedTableId}
          onChange={(e) => setSelectedTableId(e.target.value)}
          aria-label="Select table for guest list"
        >
          <option value="">Select a table ({filteredTables.length} shown)...</option>
          {filteredTables.map((t) => (
            <option key={t.id} value={t.id}>
              Table {t.tableNumber}{t.tableName ? ` — ${t.tableName}` : ''}
            </option>
          ))}
        </select>
        <button style={styles.searchBtn} onClick={fetchReport} disabled={!selectedTableId} aria-label="Load guest list">
          Load
        </button>
      </div>

      {error && <div style={styles.error} role="alert">{error}</div>}
      {loading && <div style={styles.loading}>Loading guest list...</div>}

      {!loading && hasSearched && data.length === 0 && (
        <div style={styles.empty}>No guests assigned to this table.</div>
      )}

      {!loading && data.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table} aria-label="Guest list for table">
            <thead>
              <tr>
                <th style={styles.th}>Guest Name</th>
                <th style={styles.th}>Seat</th>
                <th style={styles.th}>Chapter</th>
                <th style={styles.th}>Family Group</th>
                <th style={styles.th}>Checked In</th>
                <th style={styles.th}>Dietary</th>
                <th style={styles.th}>Preferences</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={idx}>
                  <td style={styles.td}>{row.guestName}</td>
                  <td style={styles.td}>{row.seatCompositeId}</td>
                  <td style={styles.td}>{row.chapterAffiliation || '—'}</td>
                  <td style={styles.td}>{row.familyGroup || '—'}</td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.statusBadge,
                        backgroundColor: row.checkedIn ? '#e8f5e9' : '#fff3e0',
                        color: row.checkedIn ? '#2e7d32' : '#e65100',
                      }}
                    >
                      {row.checkedIn ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td style={styles.td}>{row.dietaryPreference || '—'}</td>
                  <td style={styles.td}>{row.seatingPreferences || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Action buttons: Print and Send Notification */}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button
              style={{ padding: '0.45rem 1rem', fontSize: '0.82rem', fontWeight: 600, backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              onClick={async () => {
                const selectedTable = tables.find((t) => t.id === selectedTableId);
                const tableName = selectedTable ? `Table ${selectedTable.tableNumber}${selectedTable.tableName ? ' — ' + selectedTable.tableName : ''}` : 'Table';
                const rows = data.map((r) => `<tr><td style="padding:6px 10px;border-bottom:1px solid #ddd">${r.guestName}</td><td style="padding:6px 10px;border-bottom:1px solid #ddd">${r.seatCompositeId}</td><td style="padding:6px 10px;border-bottom:1px solid #ddd">${r.checkedIn ? 'Yes' : 'No'}</td><td style="padding:6px 10px;border-bottom:1px solid #ddd">${r.dietaryPreference || '—'}</td></tr>`).join('');
                let holdNote = '';
                try {
                  const tableDetail = await apiClient<{ seats: Array<{ seatNumber: number; status: string }> }>(`/table-management/tables/${selectedTableId}`);
                  const heldSeats = (tableDetail.seats || []).filter((s) => s.status === 'Held').sort((a, b) => a.seatNumber - b.seatNumber);
                  const blockedSeats = (tableDetail.seats || []).filter((s) => s.status === 'Blocked').sort((a, b) => a.seatNumber - b.seatNumber);
                  if (heldSeats.length > 0) holdNote += `<p style="color:#e65100;font-size:12px;margin-top:12px;">🔒 <strong>Seats on hold:</strong> ${heldSeats.map((s) => s.seatNumber).join(', ')}</p>`;
                  if (blockedSeats.length > 0) holdNote += `<p style="color:#d32f2f;font-size:12px;margin-top:4px;">🚫 <strong>Seats blocked:</strong> ${blockedSeats.map((s) => s.seatNumber).join(', ')}</p>`;
                } catch { /* print without hold info */ }
                const html = `<!DOCTYPE html><html><head><title>${tableName} - Guest List</title><style>body{font-family:sans-serif;padding:20px}table{width:100%;border-collapse:collapse}th{text-align:left;padding:8px 10px;border-bottom:2px solid #333;font-size:13px}td{font-size:12px}h1{font-size:18px;margin-bottom:4px}p{color:#666;font-size:12px;margin-bottom:16px}</style></head><body><h1>📋 ${tableName}</h1><p>${data.length} guests • Printed ${new Date().toLocaleDateString()}</p><table><thead><tr><th>Guest Name</th><th>Seat</th><th>Checked In</th><th>Dietary</th></tr></thead><tbody>${rows}</tbody></table>${holdNote}</body></html>`;
                const printWindow = window.open('', '_blank');
                if (printWindow) {
                  printWindow.document.write(html);
                  printWindow.document.close();
                  printWindow.focus();
                  setTimeout(() => printWindow.print(), 300);
                }
              }}
              aria-label="Print table guest list"
            >
              🖨️ Print
            </button>
            <button
              style={{ padding: '0.45rem 1rem', fontSize: '0.82rem', fontWeight: 600, backgroundColor: '#1565c0', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              onClick={async () => {
                const selectedTable = tables.find((t) => t.id === selectedTableId);
                const tableName = selectedTable ? `Table ${selectedTable.tableNumber}${selectedTable.tableName ? ' — ' + selectedTable.tableName : ''}` : 'Table';
                const guestNames = data.map((r, i) => `${i + 1}. ${r.guestName} — Seat ${r.seatCompositeId}`).join('\n');
                const body = `${tableName}\n\n${guestNames}\n\n—\n${data.length} guests total`;
                try {
                  const announcement = await apiClient<{ id: string }>('/announcements', {
                    method: 'POST',
                    body: { title: `📋 Table Guest List: ${tableName}`, body, audience: 'DelegatesOnly' },
                  });
                  await apiClient(`/announcements/${announcement.id}/publish`, { method: 'POST' });
                  alert('Table guest list sent as announcement to all delegates and organizers.');
                } catch (err: any) {
                  alert(err?.message || 'Failed to send notification.');
                }
              }}
              aria-label="Send table guest list as notification"
            >
              📤 Send as Notification
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Unassigned Guests Report Sub-component
type UnassignedSortKey = 'name' | 'chapter' | 'chapter+name' | 'family' | 'name+family';

function sortUnassignedGuests(guests: UnassignedGuest[], sortKey: UnassignedSortKey): UnassignedGuest[] {
  const compare = (a: UnassignedGuest, b: UnassignedGuest): number => {
    switch (sortKey) {
      case 'name':
        return (a.fullName || '').localeCompare(b.fullName || '');
      case 'chapter':
        return (a.chapterAffiliation || '').localeCompare(b.chapterAffiliation || '');
      case 'chapter+name':
        return (a.chapterAffiliation || '').localeCompare(b.chapterAffiliation || '') || (a.fullName || '').localeCompare(b.fullName || '');
      case 'family':
        return (a.familyAssociation || '').localeCompare(b.familyAssociation || '');
      case 'name+family':
        return (a.fullName || '').localeCompare(b.fullName || '') || (a.familyAssociation || '').localeCompare(b.familyAssociation || '');
      default:
        return 0;
    }
  };
  return [...guests].sort(compare);
}

function UnassignedGuestsReport() {
  const [data, setData] = useState<UnassignedGuest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortKey, setSortKey] = useState<UnassignedSortKey>('name');
  const [filterChapter, setFilterChapter] = useState<string>('');
  const [filterFamily, setFilterFamily] = useState<string>('');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiClient<UnassignedGuest[]>('/table-management/reports/unassigned');
      setData(result);
    } catch {
      setError('Failed to load unassigned guests report.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (loading) return <div style={styles.loading}>Loading unassigned guests...</div>;
  if (error) return <div style={styles.error} role="alert">{error}</div>;

  if (data.length === 0) {
    return <div style={styles.empty}>All guests have been assigned seats.</div>;
  }

  // Build unique chapter and family lists for filter dropdowns
  const chapters = Array.from(new Set(data.map(g => g.chapterAffiliation).filter(Boolean) as string[])).sort();
  const families = Array.from(new Set(data.map(g => g.familyAssociation).filter(Boolean) as string[])).sort();

  // Apply filters
  let filteredData = data;
  if (filterChapter) {
    filteredData = filteredData.filter(g => g.chapterAffiliation === filterChapter);
  }
  if (filterFamily) {
    filteredData = filteredData.filter(g => g.familyAssociation === filterFamily);
  }

  const sortedData = sortUnassignedGuests(filteredData, sortKey);

  return (
    <div style={{ overflowX: 'auto' }}>
      {/* Totals */}
      <div style={{ fontSize: '0.8rem', color: '#1a6b3c', fontWeight: 600, marginBottom: '0.5rem' }}>
        Showing {sortedData.length} of {data.length} unassigned guests
      </div>

      {/* Controls row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 500 }}>Sort by:</label>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as UnassignedSortKey)}
            style={{ fontSize: '0.8rem', padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', color: '#0f2d1f' }}
          >
            <option value="name">Name</option>
            <option value="chapter">Chapter</option>
            <option value="chapter+name">Chapter + Name</option>
            <option value="family">Family Group</option>
            <option value="name+family">Name + Family Group</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 500 }}>Chapter:</label>
          <select
            value={filterChapter}
            onChange={(e) => setFilterChapter(e.target.value)}
            style={{ fontSize: '0.8rem', padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', color: '#0f2d1f' }}
          >
            <option value="">All Chapters</option>
            {chapters.map(ch => <option key={ch} value={ch}>{ch}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 500 }}>Family:</label>
          <select
            value={filterFamily}
            onChange={(e) => setFilterFamily(e.target.value)}
            style={{ fontSize: '0.8rem', padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', color: '#0f2d1f' }}
          >
            <option value="">All Families</option>
            {families.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        {(filterChapter || filterFamily) && (
          <button
            onClick={() => { setFilterChapter(''); setFilterFamily(''); }}
            style={{ fontSize: '0.75rem', padding: '3px 8px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#f3f4f6', cursor: 'pointer', color: '#6b7280' }}
          >
            Clear filters
          </button>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
          <button
            onClick={() => {
              const rows = sortedData.map((g) => `<tr><td>${g.fullName}</td><td>${g.chapterAffiliation || '—'}</td><td>${g.ticketNumber || '—'}</td><td>${g.referenceId || '—'}</td><td>${g.familyAssociation || '—'}</td><td>${g.seatingPreferences || '—'}</td></tr>`).join('');
              const html = `<!DOCTYPE html><html><head><title>Unassigned Seats</title><style>body{font-family:sans-serif;padding:20px;}table{width:100%;border-collapse:collapse;}th{text-align:left;padding:8px 10px;border-bottom:2px solid #333;font-size:13px;}td{padding:6px 10px;border-bottom:1px solid #ddd;font-size:12px;}h1{font-size:18px;margin-bottom:4px;}p{color:#666;font-size:12px;margin-bottom:16px;}</style></head><body><h1>Unassigned Seats</h1><p>${sortedData.length} guests • Printed ${new Date().toLocaleDateString()}</p><table><thead><tr><th>Guest Name</th><th>Chapter</th><th>Ticket #</th><th>Order ID</th><th>Family Group</th><th>Preferences</th></tr></thead><tbody>${rows}</tbody></table></body></html>`;
              const w = window.open('', '_blank');
              if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
            }}
            style={{ padding: '4px 12px', fontSize: '0.78rem', fontWeight: 600, backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            aria-label="Print unassigned report"
          >🖨️ Print</button>
          <button
            onClick={() => {
              const header = 'Guest Name,Chapter,Ticket Number,Order ID,Family Group,Preferences,Accessibility\n';
              const rows = sortedData.map((g) => `"${g.fullName}","${g.chapterAffiliation || ''}","${g.ticketNumber || ''}","${g.referenceId || ''}","${g.familyAssociation || ''}","${g.seatingPreferences || ''}","${g.accessibilityNeeds || ''}"`).join('\n');
              const blob = new Blob([header + rows], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = `unassigned-seats-${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
            }}
            style={{ padding: '4px 12px', fontSize: '0.78rem', fontWeight: 600, backgroundColor: '#1565c0', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            aria-label="Download CSV"
          >📥 CSV</button>
        </div>
      </div>

      <table style={styles.table} aria-label="Unassigned seats report">
        <thead>
          <tr>
            <th style={styles.th}>Guest Name</th>
            <th style={styles.th}>Chapter</th>
            <th style={styles.th}>Ticket #</th>
            <th style={styles.th}>Order ID</th>
            <th style={styles.th}>Family Group</th>
            <th style={styles.th}>Preferences</th>
            <th style={styles.th}>Accessibility</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((guest) => (
            <tr key={guest.id}>
              <td style={styles.td}>{guest.fullName}</td>
              <td style={styles.td}>{guest.chapterAffiliation || '—'}</td>
              <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.78rem' }}>{guest.ticketNumber || '—'}</td>
              <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.75rem', color: guest.referenceId ? '#1565c0' : '#bbb' }}>{guest.referenceId || '—'}</td>
              <td style={styles.td}>{guest.familyAssociation || '—'}</td>
              <td style={styles.td}>{guest.seatingPreferences || '—'}</td>
              <td style={styles.td}>{guest.accessibilityNeeds || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Check-In Status Report Sub-component
function CheckInStatusReport() {
  const [data, setData] = useState<CheckInStatusRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiClient<CheckInStatusRow[]>('/table-management/reports/check-in-status');
      setData(result);
    } catch {
      setError('Failed to load check-in status report.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (loading) return <div style={styles.loading}>Loading check-in status...</div>;
  if (error) return <div style={styles.error} role="alert">{error}</div>;

  const statusColors: Record<string, { bg: string; color: string }> = {
    'checked-in': { bg: '#e8f5e9', color: '#2e7d32' },
    'not-arrived': { bg: '#fff3e0', color: '#e65100' },
    'no-show': { bg: '#fdecea', color: '#d32f2f' },
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={styles.table} aria-label="Check-in status report">
        <thead>
          <tr>
            <th style={styles.th}>Guest Name</th>
            <th style={styles.th}>Table</th>
            <th style={styles.th}>Seat</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Arrival Time</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => {
            const statusStyle = statusColors[row.checkInStatus] || { bg: '#f5f5f5', color: '#666' };
            return (
              <tr key={idx}>
                <td style={styles.td}>{row.name}</td>
                <td style={styles.td}>{row.tableNumber ?? '—'}</td>
                <td style={styles.td}>{row.compositeId || '—'}</td>
                <td style={styles.td}>
                  <span
                    style={{
                      ...styles.statusBadge,
                      backgroundColor: statusStyle.bg,
                      color: statusStyle.color,
                    }}
                  >
                    {row.checkInStatus}
                  </span>
                </td>
                <td style={styles.td}>
                  {row.checkedInAt
                    ? new Date(row.checkedInAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Seats Hold Report Sub-component — 2-pane layout
function SeatsHoldReport() {
  const [data, setData] = useState<Array<{ seatId: string; compositeId: string; seatNumber: number; tableNumber: number; tableName: string; zoneName: string; holdReason: string | null }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedTable, setSelectedTable] = useState<number | null>(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const held = await apiClient<typeof data>('/table-management/reports/held-seats');
      setData(held);
      // Auto-select first table
      if (held.length > 0) {
        const tables = [...new Set(held.map((s) => s.tableNumber))].sort((a, b) => a - b);
        setSelectedTable(tables[0]);
      }
    } catch { setError('Failed to load seats hold report.'); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { fetchReport(); }, [fetchReport]);

  if (loading) return <div style={styles.loading}>Loading seats hold report...</div>;
  if (error) return <div style={styles.error} role="alert">{error}</div>;
  if (data.length === 0) return <div style={styles.empty}>No seats are currently on hold.</div>;

  // Group by table
  const tableGroups = new Map<number, typeof data>();
  for (const seat of data) {
    if (!tableGroups.has(seat.tableNumber)) tableGroups.set(seat.tableNumber, []);
    tableGroups.get(seat.tableNumber)!.push(seat);
  }
  const tableList = [...tableGroups.entries()].sort((a, b) => a[0] - b[0]);
  const selectedSeats = selectedTable !== null ? (tableGroups.get(selectedTable) || []) : [];
  const selectedTableInfo = selectedSeats.length > 0 ? selectedSeats[0] : null;

  const handlePrintAll = () => {
    const headerCells = ['Seat', 'Table', 'Zone', 'Hold Reason'].map((h) => `<th>${h}</th>`).join('');
    const rows = data.map((r) => `<tr><td>${r.compositeId}</td><td>T${r.tableNumber} — ${r.tableName}</td><td>${r.zoneName}</td><td>${r.holdReason || '—'}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><title>All Seats On Hold</title><style>body{font-family:sans-serif;padding:20px;}table{width:100%;border-collapse:collapse;}th{text-align:left;padding:8px 10px;border-bottom:2px solid #333;font-size:13px;}td{padding:6px 10px;border-bottom:1px solid #ddd;font-size:12px;}h1{font-size:18px;margin-bottom:4px;}p{color:#666;font-size:12px;margin-bottom:16px;}</style></head><body><h1>🔒 All Seats On Hold</h1><p>${data.length} seats across ${tableList.length} tables • Printed ${new Date().toLocaleDateString()}</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rows}</tbody></table></body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
  };

  const handlePrintTable = () => {
    if (!selectedTableInfo) return;
    const rows = selectedSeats.map((r) => `<tr><td>${r.seatNumber}</td><td>${r.compositeId}</td><td>${r.holdReason || '—'}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><title>Held Seats — Table ${selectedTable}</title><style>body{font-family:sans-serif;padding:20px;}table{width:100%;border-collapse:collapse;}th{text-align:left;padding:8px 10px;border-bottom:2px solid #333;font-size:13px;}td{padding:6px 10px;border-bottom:1px solid #ddd;font-size:12px;}h1{font-size:18px;margin-bottom:4px;}p{color:#666;font-size:12px;margin-bottom:16px;}</style></head><body><h1>🔒 Table ${selectedTable} — ${selectedTableInfo.tableName}</h1><p>${selectedTableInfo.zoneName} Zone • ${selectedSeats.length} seat${selectedSeats.length !== 1 ? 's' : ''} on hold • Printed ${new Date().toLocaleDateString()}</p><table><thead><tr><th>Seat #</th><th>Seat ID</th><th>Hold Reason</th></tr></thead><tbody>${rows}</tbody></table></body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_DARK }}>{data.length} seat{data.length !== 1 ? 's' : ''} on hold across {tableList.length} table{tableList.length !== 1 ? 's' : ''}</span>
        <button style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }} onClick={handlePrintAll} aria-label="Print all held seats">🖨️ Print All</button>
      </div>
      <div style={{ display: 'flex', gap: '1rem', minHeight: '200px' }}>
        {/* Left pane: table summary list */}
        <div style={{ width: '220px', flexShrink: 0, borderRight: `1px solid ${BORDER_LIGHT}`, paddingRight: '0.75rem', overflowY: 'auto', maxHeight: '400px' }}>
          {tableList.map(([tableNum, seats]) => (
            <div
              key={tableNum}
              style={{ padding: '0.5rem 0.6rem', borderRadius: '4px', cursor: 'pointer', marginBottom: '4px', backgroundColor: selectedTable === tableNum ? '#e8f5e9' : 'transparent', borderLeft: selectedTable === tableNum ? `3px solid ${PRIMARY}` : '3px solid transparent' }}
              onClick={() => setSelectedTable(tableNum)}
              role="button"
              tabIndex={0}
              aria-label={`Table ${tableNum} — ${seats.length} held seats`}
              onKeyDown={(e) => { if (e.key === 'Enter') setSelectedTable(tableNum); }}
            >
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: TEXT_DARK }}>T{tableNum} — {seats[0].tableName}</div>
              <div style={{ fontSize: '0.72rem', color: TEXT_MUTED }}>{seats[0].zoneName} • {seats.length} seat{seats.length !== 1 ? 's' : ''}</div>
            </div>
          ))}
        </div>
        {/* Right pane: seat details for selected table */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {selectedTable !== null && selectedTableInfo ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 700, color: TEXT_DARK }}>Table {selectedTable} — {selectedTableInfo.tableName}</div>
                  <div style={{ fontSize: '0.75rem', color: TEXT_MUTED }}>{selectedTableInfo.zoneName} Zone • {selectedSeats.length} held</div>
                </div>
                <button style={{ padding: '0.35rem 0.7rem', fontSize: '0.75rem', fontWeight: 600, backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }} onClick={handlePrintTable} aria-label="Print this table's held seats">🖨️ Print Table</button>
              </div>
              <table style={styles.table} aria-label={`Held seats for table ${selectedTable}`}>
                <thead><tr><th style={styles.th}>Seat #</th><th style={styles.th}>Seat ID</th><th style={styles.th}>Hold Reason</th></tr></thead>
                <tbody>
                  {selectedSeats.sort((a, b) => a.seatNumber - b.seatNumber).map((s) => (
                    <tr key={s.seatId}><td style={styles.td}>{s.seatNumber}</td><td style={styles.td}>{s.compositeId}</td><td style={styles.td}>{s.holdReason || '—'}</td></tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div style={styles.empty}>Select a table from the list.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// AI Insights Report Sub-component
interface ZoneInsightData {
  zoneName: string;
  totalSeats: number;
  occupiedSeats: number;
  availableSeats: number;
  occupancyPct: number;
  tierCompliancePct: number;
  chapterMix: number;
  tableCount: number;
}

interface ChapterInsightData {
  chapter: string;
  seatedCount: number;
  zoneSpread: number;
  tableSpread: number;
  distribution: string;
  zones: string[];
}

interface NotableGuestData {
  name: string;
  tags: string[];
}

interface TableProfileData {
  tableName: string;
  tableNumber: number;
  zoneName: string;
  capacity: number;
  occupied: number;
  notableGuests: NotableGuestData[];
}

interface KeyMemberData {
  name: string;
  role: string;
  chapter: string;
  tableName: string | null;
  tableNumber: number | null;
  zoneName: string | null;
  isSeated: boolean;
  hasTicket: boolean;
}

interface InsightsReportData {
  generatedAt: string;
  zones: ZoneInsightData[];
  chapters: ChapterInsightData[];
  tableProfiles: TableProfileData[];
  keyMembers: KeyMemberData[];
  observations: string[];
}

function AiInsightsReport() {
  const [data, setData] = useState<InsightsReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ticketFilter, setTicketFilter] = useState<'all' | 'with-ticket' | 'no-ticket'>('all');
  const [viewMode, setViewMode] = useState<'report' | 'infographic'>('report');

  const handlePrintInfographic = () => {
    if (!data) return;
    const totalSeats = data.zones.reduce((s, z) => s + z.totalSeats, 0);
    const totalOccupied = data.zones.reduce((s, z) => s + z.occupiedSeats, 0);
    const overallPct = totalSeats > 0 ? Math.round((totalOccupied / totalSeats) * 100) : 0;
    const presidentsHeritage = data.keyMembers.filter((m) => m.role === 'President' || m.role === 'Heritage');
    const seatedLeaders = presidentsHeritage.filter((m) => m.isSeated).length;
    const ticketedLeaders = presidentsHeritage.filter((m) => m.hasTicket).length;
    const clustered = data.chapters.filter((c) => c.distribution === 'Clustered').length;
    const scattered = data.chapters.filter((c) => c.distribution === 'Scattered').length;
    const zoneCards = data.zones.map((z) => `<div class="zone-card"><div class="zone-name">${z.zoneName}</div><div class="zone-pct">${z.occupancyPct}%</div><div class="zone-bar"><div class="zone-fill" style="width:${z.occupancyPct}%;background:${z.occupancyPct > 90 ? '#d32f2f' : z.occupancyPct > 60 ? '#f9a825' : '#1a6b3c'}"></div></div><div class="zone-detail">${z.occupiedSeats}/${z.totalSeats} seats &bull; ${z.chapterMix} chapters &bull; Tier ${z.tierCompliancePct}%</div></div>`).join('');
    const leaderRows = presidentsHeritage.slice(0, 20).map((km) => `<tr><td>${km.name}</td><td><span class="badge ${km.role === 'President' ? 'badge-blue' : 'badge-purple'}">${km.role}</span></td><td>${km.chapter}</td><td><span class="badge ${km.hasTicket ? 'badge-green' : 'badge-orange'}">${km.hasTicket ? 'Yes' : 'No'}</span></td><td><span class="badge ${km.isSeated ? 'badge-green' : 'badge-red'}">${km.isSeated ? 'Seated' : '—'}</span></td><td>${km.tableName || '—'}</td></tr>`).join('');
    const chapterRows = data.chapters.slice(0, 12).map((ch) => `<tr><td style="font-weight:600">${ch.chapter}</td><td style="text-align:center">${ch.seatedCount}</td><td><span class="badge ${ch.distribution === 'Clustered' ? 'badge-green' : ch.distribution === 'Semi-grouped' ? 'badge-orange' : 'badge-red'}">${ch.distribution}</span></td><td style="font-size:10px;color:#666">${ch.zones.join(', ')}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><title>AI Seating Insights — Infographic</title><style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Segoe UI',system-ui,sans-serif;background:#fff;padding:32px;color:#1a1a1a}.header{text-align:center;margin-bottom:28px;border-bottom:3px solid #1a6b3c;padding-bottom:16px}.header h1{font-size:24px;color:#1a6b3c;margin-bottom:4px}.header p{font-size:12px;color:#666}.kpi-row{display:flex;gap:16px;margin-bottom:24px;flex-wrap:wrap}.kpi{flex:1;min-width:140px;background:#f0f7f4;border-radius:12px;padding:16px;text-align:center;border:1px solid #d1e7dd}.kpi-value{font-size:28px;font-weight:800;color:#1a6b3c}.kpi-label{font-size:11px;color:#666;text-transform:uppercase;letter-spacing:.5px;margin-top:4px}.section-title{font-size:14px;font-weight:700;color:#0f2d1f;margin:20px 0 10px;padding-bottom:4px;border-bottom:2px solid #e5e7eb}.zone-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:12px;margin-bottom:20px}.zone-card{background:#fafafa;border:1px solid #e5e7eb;border-radius:8px;padding:12px}.zone-name{font-weight:700;font-size:13px;color:#0f2d1f}.zone-pct{font-size:22px;font-weight:800;color:#1a6b3c;margin:4px 0}.zone-bar{height:6px;background:#e0e0e0;border-radius:3px;overflow:hidden;margin-bottom:6px}.zone-fill{height:100%;border-radius:3px}.zone-detail{font-size:10px;color:#666}.obs-box{background:#f0f7f4;border-left:4px solid #1a6b3c;border-radius:6px;padding:12px 16px;margin-bottom:20px}.obs-box ul{padding-left:18px;font-size:12px;line-height:1.8;color:#333}table{width:100%;border-collapse:collapse;font-size:11px;margin-bottom:16px}th{text-align:left;padding:6px 8px;background:#f5f5f5;border-bottom:2px solid #e0e0e0;font-weight:700;font-size:10px;text-transform:uppercase;color:#333}td{padding:5px 8px;border-bottom:1px solid #f0f0f0}.badge{display:inline-block;padding:1px 6px;border-radius:3px;font-size:10px;font-weight:600}.badge-green{background:#e8f5e9;color:#2e7d32}.badge-red{background:#fdecea;color:#d32f2f}.badge-orange{background:#fff3e0;color:#e65100}.badge-blue{background:#e8eaf6;color:#283593}.badge-purple{background:#f3e5f5;color:#6a1b9a}.two-col{display:grid;grid-template-columns:1fr 1fr;gap:20px}@media print{body{padding:16px}}@media(max-width:600px){.two-col{grid-template-columns:1fr}}</style></head><body><div class="header"><h1>🧠 AI Seating Insights</h1><p>KDU Georgia 2026 — Legacy Ball &bull; Generated ${new Date(data.generatedAt).toLocaleString()}</p></div><div class="kpi-row"><div class="kpi"><div class="kpi-value">${overallPct}%</div><div class="kpi-label">Overall Occupancy</div></div><div class="kpi"><div class="kpi-value">${totalOccupied}/${totalSeats}</div><div class="kpi-label">Seats Filled</div></div><div class="kpi"><div class="kpi-value">${data.zones.length}</div><div class="kpi-label">Zones</div></div><div class="kpi"><div class="kpi-value">${data.chapters.length}</div><div class="kpi-label">Chapters</div></div><div class="kpi"><div class="kpi-value">${ticketedLeaders}/${presidentsHeritage.length}</div><div class="kpi-label">Leaders w/ Tickets</div></div><div class="kpi"><div class="kpi-value">${seatedLeaders}</div><div class="kpi-label">Leaders Seated</div></div></div><div class="obs-box"><ul>${data.observations.map((o) => `<li>${o}</li>`).join('')}</ul></div><div class="section-title">Zone Occupancy</div><div class="zone-grid">${zoneCards}</div><div class="two-col"><div><div class="section-title">Chapter Clustering</div><div style="font-size:11px;color:#666;margin-bottom:8px">${clustered} clustered &bull; ${scattered} scattered</div><table><thead><tr><th>Chapter</th><th>Seated</th><th>Distribution</th><th>Zones</th></tr></thead><tbody>${chapterRows}</tbody></table></div><div><div class="section-title">Key Members — Presidents &amp; Heritage</div><table><thead><tr><th>Name</th><th>Role</th><th>Chapter</th><th>Ticket</th><th>Status</th><th>Table</th></tr></thead><tbody>${leaderRows}</tbody></table></div></div></body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 400); }
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiClient<InsightsReportData>('/seating-intelligence/insights-report');
      setData(result);
    } catch {
      setError('Failed to load AI insights report.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (loading) return <div style={styles.loading}>Analyzing seating chart...</div>;
  if (error) return <div style={styles.error} role="alert">{error}</div>;
  if (!data) return <div style={styles.empty}>No data available.</div>;

  const distributionColor = (d: string) => {
    if (d === 'Clustered') return { bg: '#e8f5e9', color: '#2e7d32' };
    if (d === 'Semi-grouped') return { bg: '#fff3e0', color: '#e65100' };
    return { bg: '#fdecea', color: '#d32f2f' };
  };

  const tagColor = (tag: string) => {
    if (tag === 'Patron') return { bg: '#fce4ec', color: '#880e4f' };
    if (tag === 'VIP') return { bg: '#e8eaf6', color: '#283593' };
    if (tag === 'Executive') return { bg: '#e0f2f1', color: '#004d40' };
    if (tag === 'Heritage') return { bg: '#f3e5f5', color: '#6a1b9a' };
    if (tag.includes('President')) return { bg: '#e8eaf6', color: '#1a237e' };
    if (tag.includes('Secretary')) return { bg: '#fce4ec', color: '#4a148c' };
    return { bg: '#f5f5f5', color: '#333' };
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ fontSize: '0.78rem', color: TEXT_MUTED }}>
          Generated: {new Date(data.generatedAt).toLocaleString()}
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '2px', background: '#f5f5f5', borderRadius: '6px', padding: '2px' }}>
            <button onClick={() => setViewMode('report')} style={{ padding: '4px 10px', fontSize: '0.72rem', fontWeight: 600, borderRadius: '4px', border: 'none', cursor: 'pointer', background: viewMode === 'report' ? PRIMARY : 'transparent', color: viewMode === 'report' ? '#fff' : '#666' }}>Report</button>
            <button onClick={() => setViewMode('infographic')} style={{ padding: '4px 10px', fontSize: '0.72rem', fontWeight: 600, borderRadius: '4px', border: 'none', cursor: 'pointer', background: viewMode === 'infographic' ? PRIMARY : 'transparent', color: viewMode === 'infographic' ? '#fff' : '#666' }}>Infographic</button>
          </div>
          {viewMode === 'infographic' && (
            <button onClick={handlePrintInfographic} style={{ padding: '4px 10px', fontSize: '0.72rem', fontWeight: 600, backgroundColor: '#1565c0', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }} aria-label="Print infographic">🖨️ Print</button>
          )}
          <button style={styles.searchBtn} onClick={fetchReport} aria-label="Refresh insights">
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Infographic View */}
      {viewMode === 'infographic' && (() => {
        const totalSeats = data.zones.reduce((s, z) => s + z.totalSeats, 0);
        const totalOccupied = data.zones.reduce((s, z) => s + z.occupiedSeats, 0);
        const overallPct = totalSeats > 0 ? Math.round((totalOccupied / totalSeats) * 100) : 0;
        const presidentsHeritage = data.keyMembers.filter((m) => m.role === 'President' || m.role === 'Heritage');
        const seatedLeaders = presidentsHeritage.filter((m) => m.isSeated).length;
        const ticketedLeaders = presidentsHeritage.filter((m) => m.hasTicket).length;
        const clustered = data.chapters.filter((c) => c.distribution === 'Clustered').length;
        const scattered = data.chapters.filter((c) => c.distribution === 'Scattered').length;
        const kpiBox: React.CSSProperties = { flex: 1, minWidth: '110px', background: '#f0f7f4', borderRadius: '12px', padding: '14px', textAlign: 'center', border: '1px solid #d1e7dd' };
        return (
          <div>
            {/* KPI Row */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div style={kpiBox}><div style={{ fontSize: '1.5rem', fontWeight: 800, color: PRIMARY }}>{overallPct}%</div><div style={{ fontSize: '0.65rem', color: TEXT_MUTED, textTransform: 'uppercase', marginTop: '2px' }}>Occupancy</div></div>
              <div style={kpiBox}><div style={{ fontSize: '1.5rem', fontWeight: 800, color: PRIMARY }}>{totalOccupied}/{totalSeats}</div><div style={{ fontSize: '0.65rem', color: TEXT_MUTED, textTransform: 'uppercase', marginTop: '2px' }}>Seats Filled</div></div>
              <div style={kpiBox}><div style={{ fontSize: '1.5rem', fontWeight: 800, color: PRIMARY }}>{data.chapters.length}</div><div style={{ fontSize: '0.65rem', color: TEXT_MUTED, textTransform: 'uppercase', marginTop: '2px' }}>Chapters</div></div>
              <div style={kpiBox}><div style={{ fontSize: '1.5rem', fontWeight: 800, color: PRIMARY }}>{ticketedLeaders}/{presidentsHeritage.length}</div><div style={{ fontSize: '0.65rem', color: TEXT_MUTED, textTransform: 'uppercase', marginTop: '2px' }}>Leaders w/ Ticket</div></div>
              <div style={kpiBox}><div style={{ fontSize: '1.5rem', fontWeight: 800, color: PRIMARY }}>{seatedLeaders}</div><div style={{ fontSize: '0.65rem', color: TEXT_MUTED, textTransform: 'uppercase', marginTop: '2px' }}>Leaders Seated</div></div>
            </div>
            {/* Observations */}
            <div style={{ padding: '0.75rem', backgroundColor: '#f0f7f4', borderRadius: '6px', borderLeft: `4px solid ${PRIMARY}`, marginBottom: '16px' }}>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.8rem', lineHeight: 1.7, color: '#333' }}>
                {data.observations.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
            {/* Zone Grid */}
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '8px' }}>Zone Occupancy</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px', marginBottom: '20px' }}>
              {data.zones.map((z) => (
                <div key={z.zoneName} style={{ background: '#fafafa', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', padding: '10px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.78rem', color: TEXT_DARK }}>{z.zoneName}</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 800, color: PRIMARY, margin: '2px 0' }}>{z.occupancyPct}%</div>
                  <div style={{ height: '5px', background: '#e0e0e0', borderRadius: '3px', overflow: 'hidden', marginBottom: '4px' }}>
                    <div style={{ height: '100%', borderRadius: '3px', width: `${z.occupancyPct}%`, background: z.occupancyPct > 90 ? '#d32f2f' : z.occupancyPct > 60 ? '#f9a825' : PRIMARY }} />
                  </div>
                  <div style={{ fontSize: '0.65rem', color: TEXT_MUTED }}>{z.occupiedSeats}/{z.totalSeats} seats &bull; {z.chapterMix} ch &bull; Tier {z.tierCompliancePct}%</div>
                </div>
              ))}
            </div>
            {/* Chapter Summary Pills */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <div style={{ padding: '8px 14px', background: '#e8f5e9', borderRadius: '8px', fontSize: '0.78rem', color: '#2e7d32', fontWeight: 600 }}>{clustered} Clustered</div>
              <div style={{ padding: '8px 14px', background: '#fff3e0', borderRadius: '8px', fontSize: '0.78rem', color: '#e65100', fontWeight: 600 }}>{data.chapters.filter((c) => c.distribution === 'Semi-grouped').length} Semi-grouped</div>
              <div style={{ padding: '8px 14px', background: '#fdecea', borderRadius: '8px', fontSize: '0.78rem', color: '#d32f2f', fontWeight: 600 }}>{scattered} Scattered</div>
            </div>
            {/* Print CTA */}
            <div style={{ textAlign: 'center', padding: '16px', background: '#f9fafb', borderRadius: '8px', border: `1px dashed ${BORDER_LIGHT}` }}>
              <div style={{ fontSize: '0.82rem', color: TEXT_MUTED, marginBottom: '8px' }}>Print the full infographic with all details</div>
              <button onClick={handlePrintInfographic} style={{ padding: '8px 24px', fontSize: '0.85rem', fontWeight: 700, backgroundColor: '#1565c0', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>🖨️ Print Infographic</button>
            </div>
          </div>
        );
      })()}

      {/* Report View */}
      {viewMode === 'report' && (<>

      {/* AI Observations */}
      <div style={{ marginBottom: '1.25rem', padding: '0.75rem', backgroundColor: '#f0f7f4', borderRadius: '6px', borderLeft: `4px solid ${PRIMARY}` }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '0.5rem' }}>
          🧠 AI Observations
        </div>
        <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.82rem', lineHeight: '1.6', color: '#333' }}>
          {data.observations.map((obs, idx) => (
            <li key={idx}>{obs}</li>
          ))}
        </ul>
      </div>

      {/* Zone Analysis */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '0.5rem' }}>
          Zone Analysis
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table} aria-label="Zone analysis">
            <thead>
              <tr>
                <th style={styles.th}>Zone</th>
                <th style={styles.th}>Tables</th>
                <th style={styles.th}>Seats</th>
                <th style={styles.th}>Occupied</th>
                <th style={styles.th}>Available</th>
                <th style={styles.th}>Occupancy %</th>
                <th style={styles.th}>Tier Compliance</th>
                <th style={styles.th}>Chapter Mix</th>
              </tr>
            </thead>
            <tbody>
              {data.zones.map((z) => (
                <tr key={z.zoneName}>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{z.zoneName}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{z.tableCount}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{z.totalSeats}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{z.occupiedSeats}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{z.availableSeats}</td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <div style={{ width: '50px', height: '8px', backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${z.occupancyPct}%`, height: '100%', backgroundColor: z.occupancyPct > 90 ? '#d32f2f' : z.occupancyPct > 60 ? '#f9a825' : PRIMARY, borderRadius: '4px' }} />
                      </div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{z.occupancyPct}%</span>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: z.tierCompliancePct === 100 ? '#e8f5e9' : z.tierCompliancePct >= 80 ? '#fff3e0' : '#fdecea',
                      color: z.tierCompliancePct === 100 ? '#2e7d32' : z.tierCompliancePct >= 80 ? '#e65100' : '#d32f2f',
                    }}>
                      {z.tierCompliancePct}%
                    </span>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{z.chapterMix} chapter{z.chapterMix !== 1 ? 's' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Chapter Analysis */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '0.5rem' }}>
          Chapter Analysis — Clustering
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table} aria-label="Chapter clustering analysis">
            <thead>
              <tr>
                <th style={styles.th}>Chapter</th>
                <th style={styles.th}>Seated</th>
                <th style={styles.th}>Zones</th>
                <th style={styles.th}>Tables</th>
                <th style={styles.th}>Distribution</th>
                <th style={styles.th}>Zone Names</th>
              </tr>
            </thead>
            <tbody>
              {data.chapters.map((ch) => {
                const dStyle = distributionColor(ch.distribution);
                return (
                  <tr key={ch.chapter}>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{ch.chapter}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{ch.seatedCount}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{ch.zoneSpread}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{ch.tableSpread}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.statusBadge, backgroundColor: dStyle.bg, color: dStyle.color }}>
                        {ch.distribution}
                      </span>
                    </td>
                    <td style={{ ...styles.td, fontSize: '0.75rem', color: TEXT_MUTED }}>{ch.zones.join(', ')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Table Profiles (Notable Guests) */}
      {data.tableProfiles.length > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '0.5rem' }}>
            Table Profiles — Notable Guests
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table} aria-label="Table profiles with notable guests">
              <thead>
                <tr>
                  <th style={styles.th}>Table</th>
                  <th style={styles.th}>Zone</th>
                  <th style={styles.th}>Occupancy</th>
                  <th style={styles.th}>Notable Guests</th>
                </tr>
              </thead>
              <tbody>
                {data.tableProfiles.map((tp) => (
                  <tr key={`${tp.tableNumber}-${tp.zoneName}`}>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{tp.tableName}</td>
                    <td style={styles.td}>{tp.zoneName}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{tp.occupied}/{tp.capacity}</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                        {tp.notableGuests.map((ng, idx) => {
                          const tc = tagColor(ng.tags[0]);
                          return (
                            <span key={idx} style={{ fontSize: '0.72rem', padding: '2px 6px', borderRadius: '3px', backgroundColor: tc.bg, color: tc.color }}>
                              {ng.name} ({ng.tags.join(', ')})
                            </span>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Key Members — Presidents & Heritage */}
      {data.keyMembers.length > 0 && (() => {
        const roleFiltered = data.keyMembers.filter((km) => km.role === 'President' || km.role === 'Heritage');
        const filteredKm = ticketFilter === 'all' ? roleFiltered
          : ticketFilter === 'with-ticket' ? roleFiltered.filter((m) => m.hasTicket)
          : roleFiltered.filter((m) => !m.hasTicket);
        return roleFiltered.length > 0 ? (
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: TEXT_DARK }}>
              Key Members — Presidents &amp; Heritage
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              {(['all', 'with-ticket', 'no-ticket'] as const).map((f) => (
                <button key={f} onClick={() => setTicketFilter(f)} style={{ padding: '3px 10px', borderRadius: '12px', border: ticketFilter === f ? `1px solid ${PRIMARY}` : '1px solid #d1d5db', background: ticketFilter === f ? '#e8f5e9' : '#fff', color: ticketFilter === f ? PRIMARY : TEXT_MUTED, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer' }}>
                  {f === 'all' ? 'All' : f === 'with-ticket' ? 'Has Ticket' : 'No Ticket'}
                </button>
              ))}
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table} aria-label="Chapter leadership seating">
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Chapter</th>
                  <th style={styles.th}>Ticket</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Table</th>
                  <th style={styles.th}>Zone</th>
                </tr>
              </thead>
              <tbody>
                {filteredKm.map((km, idx) => (
                  <tr key={idx} style={{ backgroundColor: km.isSeated ? '' : '#fff8f8' }}>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{km.name}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        backgroundColor: km.role === 'President' ? '#e8eaf6' : '#f3e5f5',
                        color: km.role === 'President' ? '#283593' : '#6a1b9a',
                      }}>
                        {km.role}
                      </span>
                    </td>
                    <td style={styles.td}>{km.chapter}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        backgroundColor: km.hasTicket ? '#e8f5e9' : '#fff3e0',
                        color: km.hasTicket ? '#2e7d32' : '#e65100',
                      }}>
                        {km.hasTicket ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        backgroundColor: km.isSeated ? '#e8f5e9' : '#fdecea',
                        color: km.isSeated ? '#2e7d32' : '#d32f2f',
                      }}>
                        {km.isSeated ? 'Seated' : 'Not Seated'}
                      </span>
                    </td>
                    <td style={styles.td}>{km.tableName || '—'}</td>
                    <td style={styles.td}>{km.zoneName || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        ) : null;
      })()}
      </>)}
    </div>
  );
}

// Main AuditLogPanel component
export function AuditLogPanel() {
  const [activeView, setActiveView] = useState<ReportView>('audit-log');

  const REPORT_VIEWS: { key: ReportView; label: string }[] = [
    { key: 'audit-log', label: 'Audit Log' },
    { key: 'occupancy', label: 'Occupancy' },
    { key: 'chapter-distribution', label: 'Chapter Distribution' },
    { key: 'guest-list', label: 'Table Guest List' },
    { key: 'unassigned', label: 'Unassigned Seats' },
    { key: 'check-in-status', label: 'Check-In Status' },
    { key: 'seats-hold', label: 'Seats Hold' },
    { key: 'print-tables', label: 'Print Tables' },
    { key: 'ai-insights', label: 'AI Insights' },
    { key: 'daily-report', label: 'Daily Activity' },
  ];

  return (
    <div style={styles.container}>
      <nav style={styles.tabs} aria-label="Report views">
        {REPORT_VIEWS.map((view) => (
          <button
            key={view.key}
            style={activeView === view.key ? styles.tabActive : styles.tab}
            onClick={() => setActiveView(view.key)}
            aria-current={activeView === view.key ? 'page' : undefined}
          >
            {view.label}
          </button>
        ))}
      </nav>

      {activeView === 'audit-log' && <AuditLogView />}
      {activeView === 'occupancy' && <OccupancyReport />}
      {activeView === 'chapter-distribution' && <ChapterDistributionReport />}
      {activeView === 'guest-list' && <GuestListReport />}
      {activeView === 'unassigned' && <UnassignedGuestsReport />}
      {activeView === 'check-in-status' && <CheckInStatusReport />}
      {activeView === 'seats-hold' && <SeatsHoldReport />}
      {activeView === 'print-tables' && <PrintTablesReport />}
      {activeView === 'ai-insights' && <AiInsightsReport />}
      {activeView === 'daily-report' && <DailyActivityReport />}
    </div>
  );
}
